import {
  Card,
  Flex,
  Heading,
  Link as RadixLink,
  Separator,
  Text,
} from "@radix-ui/themes";
import { Link } from "react-router-dom";

const UPDATED_AT = "April 30, 2026";

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section style={{ display: "grid", gap: 8 }}>
      <Heading as="h2" size="4">
        {title}
      </Heading>
      <Text as="div" size="2" color="gray" style={{ lineHeight: 1.65 }}>
        {children}
      </Text>
    </section>
  );
}

function BulletList({ items }: { items: React.ReactNode[] }) {
  return (
    <ul style={{ margin: "8px 0 0", paddingLeft: 20 }}>
      {items.map((item, index) => (
        <li key={index} style={{ marginBottom: 8 }}>
          {item}
        </li>
      ))}
    </ul>
  );
}

export default function PrivacyNotice() {
  return (
    <Flex
      justify="center"
      style={{
        minHeight: "100vh",
        background: "var(--color-background)",
        padding: "48px 20px",
      }}
    >
      <Card size="4" style={{ width: "min(860px, 100%)", padding: 32 }}>
        <Flex direction="column" gap="5">
          <div>
            <Text size="1" color="gray" weight="medium">
              Thread operational privacy notice
            </Text>
            <Heading as="h1" size="7" style={{ marginTop: 8 }}>
              Activity logging and access analytics
            </Heading>
            <Text as="p" size="2" color="gray" style={{ lineHeight: 1.65 }}>
              Last updated: {UPDATED_AT}. This notice supplements the{" "}
              <RadixLink
                href="https://www.flexcompute.com/privacy/"
                target="_blank"
                rel="noreferrer"
              >
                Flexcompute Privacy Policy
              </RadixLink>{" "}
              for Thread sign-in, presentation access, and app activity records.
            </Text>
          </div>

          <Separator size="4" />

          <Section title="What Thread Records">
            Thread records operational activity needed to run, secure, support, and improve
            the service. This can include:
            <BulletList
              items={[
                "Sign-in identity when available, such as email address, user ID, and authentication method.",
                "Presentation or app pages viewed, timestamps, page titles, project IDs, and presentation slugs.",
                "Basic technical metadata such as browser user agent, referrer, and a keyed hash of the client host for anonymous aggregation. The Thread activity register does not store raw IP addresses.",
                "Access events from signed invite links, email-code sign-in, Google sign-in, Flow360 sign-in, and other supported Thread authentication paths.",
              ]}
            />
          </Section>

          <Section title="Why We Use It">
            We use these records for security monitoring, access control, support and
            troubleshooting, customer-facing presentation engagement analytics, service
            reliability, and internal product improvement. For EU/UK data-protection
            purposes, we rely on performance of a contract where Thread is provided under
            agreement, Flexcompute's legitimate interests in securing and improving Thread,
            legal obligations where required, and consent where a specific optional
            analytics or cookie tool requires it.
          </Section>

          <Section title="Retention">
            Thread activity-register events are retained for up to 180 days by default and
            then pruned. Some security, infrastructure, billing, backup, or legal records may
            be retained separately under Flexcompute's broader operational, contractual, or
            legal retention requirements.
          </Section>

          <Section title="Cookies And Local Storage">
            Thread currently uses necessary browser storage for sign-in and access, including
            session storage for Flow360 browser tokens and a short-lived signed-invite cookie
            so presentation assets can load after an invite link is accepted. The current
            Thread web app does not use optional third-party analytics cookies such as Google
            Analytics or PostHog. If Thread later uses optional analytics or advertising
            cookies, they will be disclosed and consent-gated where required.
          </Section>

          <Section title="Who Can See Activity Records">
            Thread activity dashboards are limited to Flexcompute staff. We may also use
            service providers that host, secure, authenticate, email, store, or operate Thread
            under contract. Customer engineering data inside Thread remains governed by the
            applicable contract, workspace permissions, and Flexcompute's security controls.
          </Section>

          <Section title="Your Choices And Rights">
            Depending on your location, you may have rights to request access, correction,
            deletion, restriction, portability, objection, or withdrawal of consent. You can
            raise privacy questions or rights requests through{" "}
            <RadixLink href="mailto:info@flexcompute.com">info@flexcompute.com</RadixLink>.
            California residents and EU/UK/EEA users can also refer to the{" "}
            <RadixLink
              href="https://www.flexcompute.com/privacy/"
              target="_blank"
              rel="noreferrer"
            >
              Flexcompute Privacy Policy
            </RadixLink>{" "}
            for the broader rights and contact process.
          </Section>

          <Section title="Automated Decisions">
            Thread activity logging is not used to make automated decisions with legal or
            similarly significant effects about visitors. Activity metrics may be aggregated
            to understand access, support needs, and product usage.
          </Section>

          <Separator size="4" />

          <Flex justify="between" align="center" gap="3" wrap="wrap">
            <Text size="1" color="gray">
              This operational notice summarizes Thread-specific activity logging.
            </Text>
            <RadixLink asChild size="2">
              <Link to="/">Back to Thread</Link>
            </RadixLink>
          </Flex>
        </Flex>
      </Card>
    </Flex>
  );
}
